import React, { useState, useEffect, useCallback } from 'react';
import { format, startOfWeek, addDays, addWeeks } from 'date-fns';
import { es } from 'date-fns/locale';
import { weeklyScheduleApi, scheduleApi } from '../../lib/api';
import { absencesApi, Absence, AbsenceType } from '../../lib/api/absences.api';
import { Schedule, WeeklySchedule, Employee } from '../../types/weeklySchedule';
import Button from '../common/Button';
import Loader from '../common/Loader';
import PrintModal from './PrintModal';
import {
    transformCalendarToPrintData,
    generatePrintHTML,
    PrintOptions
} from '../../lib/print/printCalendarService';

// Función para generar clases CSS a partir de un color HEX
const getColorClasses = (color: string) => {
    // Convertir HEX a RGB para calcular colores claros y oscuros
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Mantener el color original pero con mayor opacidad para el fondo (85% del color original + 15% de blanco)
    const lightR = Math.round(r * 0.85 + 255 * 0.15);
    const lightG = Math.round(g * 0.85 + 255 * 0.15);
    const lightB = Math.round(b * 0.85 + 255 * 0.15);

    // Usar el color original para el borde pero un poco más oscuro (90% del color original)
    const darkR = Math.round(r * 0.9);
    const darkG = Math.round(g * 0.9);
    const darkB = Math.round(b * 0.9);

    // Determinar si el texto debe ser claro u oscuro
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    const textColor = brightness > 128 ? 'text-gray-900' : 'text-white';
    const subTextColor = brightness > 128 ? 'text-gray-700' : 'text-gray-200';

    return {
        background: `rgb(${lightR}, ${lightG}, ${lightB})`,
        border: `rgb(${darkR}, ${darkG}, ${darkB})`,
        textColor,
        subTextColor
    };
};

// Función para obtener estilos en línea basados en el color del horario
const getScheduleStyles = (schedule: Schedule) => {
    const color = schedule.color || '#3B82F6';
    const colorClasses = getColorClasses(color);

    return {
        backgroundColor: colorClasses.background,
        borderColor: colorClasses.border,
        color: colorClasses.textColor
    };
};


// Función para calcular la diferencia en horas entre dos tiempos
const calculateHoursDifference = (startTime: string, endTime: string): number => {
    const [startHour, startMinute] = startTime.split(':').map(Number);
    const [endHour, endMinute] = endTime.split(':').map(Number);

    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;

    // Para turnos nocturnos que cruzan medianoche, calcular correctamente
    if (endMinutes <= startMinutes) {
        // Es un turno nocturno que cruza medianoche
        return ((24 * 60) - startMinutes + endMinutes) / 60;
    }

    return (endMinutes - startMinutes) / 60;
};

// Función para determinar si un horario es nocturno (cruza medianoche)
const isNightShift = (startTime: string, endTime: string): boolean => {
    const [startHour] = startTime.split(':').map(Number);
    const [endHour] = endTime.split(':').map(Number);

    // Un turno es nocturno si la hora de inicio es mayor o igual a la hora de fin
    // Esto indica que cruza medianoche (ej: 22:00 a 06:00)
    return startHour >= endHour && startHour >= 22;
};

const getAbsenceTypeLabel = (type: AbsenceType): string => {
    const labels: Record<AbsenceType, string> = {
        [AbsenceType.VACATION]: 'Vacaciones',
        [AbsenceType.SICK_LEAVE]: 'Baja Médica',
        [AbsenceType.PERSONAL]: 'Personal',
        [AbsenceType.MATERNITY]: 'Maternidad',
        [AbsenceType.PATERNITY]: 'Paternidad',
        [AbsenceType.BEREAVEMENT]: 'Duelo',
        [AbsenceType.UNPAID]: 'No Remunerada',
        [AbsenceType.TRAINING]: 'Formación',
        [AbsenceType.COMPANY_CLOSURE]: 'Cierre de Empresa',
    };
    return labels[type] || type;
};

// Tipos para el calendario semanal
interface WeeklyCalendarProps {
    employees: Employee[];
    filterDepartment?: string;
    filterPosition?: string;
}

// Componente principal del calendario semanal
const WeeklyCalendarSimple: React.FC<WeeklyCalendarProps> = ({ employees, filterDepartment, filterPosition }) => {

    const [currentWeek, setCurrentWeek] = useState<Date>(startOfWeek(new Date(), { weekStartsOn: 1 }));
    const [schedules, setSchedules] = useState<Schedule[]>([]);
    const [weeklySchedules, setWeeklySchedules] = useState<WeeklySchedule[]>([]);
    const [absences, setAbsences] = useState<Absence[]>([]);
    const [selectedSchedule, setSelectedSchedule] = useState<string | 'REST' | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isCopying, setIsCopying] = useState(false);
    const [printModalOpen, setPrintModalOpen] = useState(false);
    const [selectionMode, setSelectionMode] = useState(false);
    const [sourceEmployeeId, setSourceEmployeeId] = useState<string | null>(null);
    const [sourceDay, setSourceDay] = useState<number | null>(null);
    const [sourceSchedules, setSourceSchedules] = useState<WeeklySchedule[] | null>(null);
    const [destinationDays, setDestinationDays] = useState<Set<number>>(new Set());


    // Generar los días de la semana actual
    const generateWeekDays = useCallback((weekStart: Date) => {
        const days = [];
        for (let i = 0; i < 7; i++) {
            const date = addDays(weekStart, i);
            const dayOfWeek = date.getDay(); // Usar getDay() para coincidir con backend
            days.push({
                date: format(date, 'yyyy-MM-dd'),
                dayOfWeek: dayOfWeek, // Usar el valor real de getDay()
                dayName: format(date, 'EEEE', { locale: es }),
            });
        }
        return days;
    }, []);

    const weekDays = generateWeekDays(currentWeek);
    const weekStartString = format(currentWeek, 'yyyy-MM-dd');

    // Filtrar empleados por departamento y posición
    const filteredEmployees = employees.filter(employee => {
        const matchesDepartment = !filterDepartment || employee.departmentModel?.id === filterDepartment;
        const matchesPosition = !filterPosition || employee.position === filterPosition;
        return matchesDepartment && matchesPosition;
    });

    // Cargar datos
    const loadData = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Cargar horarios disponibles
            const schedulesResponse = await scheduleApi.getSchedules();
            if (schedulesResponse.success && schedulesResponse.data) {
                const schedulesData = schedulesResponse.data.schedules || [];
                setSchedules(schedulesData);
            }

            // Cargar asignaciones semanales de todos los empleados
            const weeklySchedulesResponse = await weeklyScheduleApi.getCompanyWeeklySchedules(weekStartString);
            if (weeklySchedulesResponse.success && weeklySchedulesResponse.data) {
                const weeklyData = (weeklySchedulesResponse.data as any) || [];

                setWeeklySchedules(weeklyData);
            }

            // Cargar ausencias para la semana actual
            const weekEnd = addDays(currentWeek, 6);
            const startDateStr = format(currentWeek, 'yyyy-MM-dd');
            const endDateStr = format(weekEnd, 'yyyy-MM-dd');
            const absencesResponse = await absencesApi.getAbsences({
                startDate: startDateStr,
                endDate: endDateStr,
            });
            const allAbsences = absencesResponse.absences || [];
            console.log('📅 Ausencias cargadas:', allAbsences.length, 'ausencias');
            console.log('📅 Semana:', startDateStr, 'a', endDateStr);
            setAbsences(allAbsences);
        } catch (err) {
            console.error('Error al cargar datos del calendario:', err);
            setError('No se pudieron cargar los datos del calendario');
        } finally {
            setIsLoading(false);
        }
    }, [weekStartString, currentWeek]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const getWeeklySchedulesForEmployeeAndDay = useCallback((
        employeeId: string,
        dayOfWeek: number
    ) => {
        return weeklySchedules.filter(
            ws => ws.employeeId === employeeId && ws.dayOfWeek === dayOfWeek
        );
    }, [weeklySchedules]);

    // Obtener ausencias para un empleado en una fecha específica
    const getAbsencesForEmployeeAndDate = useCallback((
        employeeId: string,
        dateStr: string
    ) => {
        const filtered = absences.filter(
            a => a.employeeId === employeeId &&
                 (a.status === 'APPROVED' || a.status === 'PENDING') &&
                 new Date(a.startDate) <= new Date(dateStr) &&
                 new Date(a.endDate) >= new Date(dateStr)
        );
        if (filtered.length > 0) {
            console.log(`📅 Ausencias para empleado ${employeeId} en ${dateStr}:`, filtered);
        }
        return filtered;
    }, [absences]);

    const handleCellClick = useCallback(async (employeeId: string, dayOfWeek: number) => {
        if (selectionMode) {
            const cellSchedules = getWeeklySchedulesForEmployeeAndDay(employeeId, dayOfWeek);

            if (sourceEmployeeId === null) {
                if (cellSchedules.length > 0) {
                    setSourceEmployeeId(employeeId);
                    setSourceDay(dayOfWeek);
                    setSourceSchedules(cellSchedules);
                } else {
                    alert('Selecciona un día con un turno asignado como origen.');
                }
            } else if (sourceEmployeeId === employeeId) {
                if (dayOfWeek === sourceDay) {
                    setSourceEmployeeId(null);
                    setSourceDay(null);
                    setSourceSchedules(null);
                    setDestinationDays(new Set());
                } else {
                    const newDestinations = new Set(destinationDays);
                    if (newDestinations.has(dayOfWeek)) {
                        newDestinations.delete(dayOfWeek);
                    } else {
                        newDestinations.add(dayOfWeek);
                    }
                    setDestinationDays(newDestinations);
                }
            } else {
                alert('Solo puedes seleccionar destinos del mismo empleado del día de origen.');
            }
        } else if (selectedSchedule) {
            await handleAssignSchedule(employeeId, dayOfWeek, selectedSchedule === 'REST' ? null : selectedSchedule);
        }
    }, [selectionMode, sourceEmployeeId, sourceDay, destinationDays, getWeeklySchedulesForEmployeeAndDay, selectedSchedule]);

    const handleAssignSchedule = useCallback(async (employeeId: string, dayOfWeek: number, scheduleId: string | null) => {
        try {
            if (scheduleId === null) {
                await weeklyScheduleApi.upsertWeeklySchedule({
                    employeeId,
                    weekStart: weekStartString,
                    dayOfWeek,
                    scheduleId: null,
                    active: true,
                    notes: 'Día de descanso'
                });
            } else {
                await weeklyScheduleApi.upsertWeeklySchedule({
                    employeeId,
                    weekStart: weekStartString,
                    dayOfWeek,
                    scheduleId,
                    active: true,
                });
            }
            await loadData();
            setSelectedSchedule(null);
        } catch (err) {
            console.error('Error al asignar horario:', err);
            setError('No se pudo asignar el horario');
        }
    }, [weekStartString, loadData]);

    const handleRemoveWeeklySchedule = useCallback(async (weeklyScheduleId: string) => {
        try {
            await weeklyScheduleApi.deleteWeeklySchedule(weeklyScheduleId);
            await loadData();
        } catch (err) {
            console.error('Error al eliminar asignación:', err);
            setError('No se pudo eliminar la asignación');
        }
    }, [loadData]);

    const handleCopyToDestinations = useCallback(async () => {
        if (!sourceSchedules || destinationDays.size === 0 || !sourceEmployeeId) {
            return;
        }

        if (!confirm(`¿Copiar ${sourceSchedules.length} turno(s) a ${destinationDays.size} día(s)? Los turnos existentes en los días de destino serán eliminados.`)) {
            return;
        }

        try {
            for (const destDay of destinationDays) {
                console.log(`Procesando día ${destDay}`);

                const existingSchedules = getWeeklySchedulesForEmployeeAndDay(sourceEmployeeId, destDay);
                console.log(`Encontrados ${existingSchedules.length} horarios existentes en día ${destDay}`);

                for (const existingSchedule of existingSchedules) {
                    console.log(`Eliminando horario existente: ${existingSchedule.id}`);
                    try {
                        await weeklyScheduleApi.deleteWeeklySchedule(existingSchedule.id);
                    } catch (deleteError) {
                        console.error(`Error eliminando horario ${existingSchedule.id}:`, deleteError);
                    }
                }

                for (const sourceSchedule of sourceSchedules) {
                    console.log(`Copiando horario ${sourceSchedule.scheduleId} al día ${destDay}`);
                    try {
                        await weeklyScheduleApi.upsertWeeklySchedule({
                            employeeId: sourceSchedule.employeeId,
                            weekStart: weekStartString,
                            dayOfWeek: destDay,
                            scheduleId: sourceSchedule.scheduleId,
                            active: true,
                            notes: sourceSchedule.notes
                        });
                    } catch (createError) {
                        console.error(`Error creando horario en día ${destDay}:`, createError);
                        throw createError;
                    }
                }
            }
            await loadData();
            setSelectionMode(false);
            setSourceEmployeeId(null);
            setSourceDay(null);
            setSourceSchedules(null);
            setDestinationDays(new Set());
        } catch (error) {
            console.error('Error al copiar turno:', error);
            alert(`Error al copiar el turno: ${error instanceof Error ? error.message : 'Error desconocido'}`);
        }
    }, [sourceSchedules, destinationDays, weekDays, weekStartString, loadData, sourceEmployeeId, getWeeklySchedulesForEmployeeAndDay]);

    // Calcular horas totales por día para todos los empleados
    const calculateDailyHours = useCallback((dayOfWeek: number) => {
        let totalHours = 0;

        filteredEmployees.forEach(employee => {
            const daySchedules = getWeeklySchedulesForEmployeeAndDay(employee.id, dayOfWeek);
            daySchedules.forEach(ws => {
                if (ws.schedule?.startTime && ws.schedule?.endTime) {
                    totalHours += calculateHoursDifference(ws.schedule.startTime, ws.schedule.endTime);
                }
            });
        });

        return totalHours;
    }, [employees, getWeeklySchedulesForEmployeeAndDay]);

    // Calcular horas totales de la semana
    const calculateWeeklyTotalHours = useCallback(() => {
        let totalHours = 0;

        for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
            totalHours += calculateDailyHours(dayOfWeek);
        }

        return totalHours;
    }, [calculateDailyHours]);

    // Calcular horas semanales para un empleado específico
    const calculateEmployeeWeeklyHours = useCallback((employeeId: string) => {
        let totalHours = 0;

        for (let dayOfWeek = 0; dayOfWeek < 7; dayOfWeek++) {
            const daySchedules = getWeeklySchedulesForEmployeeAndDay(employeeId, dayOfWeek);
            daySchedules.forEach(ws => {
                if (ws.schedule?.startTime && ws.schedule?.endTime) {
                    totalHours += calculateHoursDifference(ws.schedule.startTime, ws.schedule.endTime);
                }
            });
        }

        return totalHours;
    }, [getWeeklySchedulesForEmployeeAndDay]);

    // Obtener el nombre del departamento filtrado
    const getFilterDepartmentName = useCallback(() => {
        if (!filterDepartment) return null;
        const filteredDept = employees.find(emp => emp.departmentModel?.id === filterDepartment);
        return filteredDept?.departmentModel?.name || null;
    }, [filterDepartment, employees]);

    // Función para abrir el modal de impresión
    const handleOpenPrintModal = useCallback(() => {
        setPrintModalOpen(true);
    }, []);

    // Función para imprimir el calendario con las opciones seleccionadas
    const handlePrint = useCallback((options: PrintOptions) => {
        try {
            // Transformar datos a formato de impresión
            const printData = transformCalendarToPrintData(
                filteredEmployees,
                weeklySchedules,
                schedules,
                currentWeek,
                options,
                getFilterDepartmentName() || undefined
            );

            // Generar HTML de impresión
            const printHTML = generatePrintHTML(printData);

            // Crear ventana de impresión
            const printWindow = window.open('', '_blank', 'width=1200,height=800');

            if (!printWindow) {
                alert('No se pudo abrir la ventana de impresión. Por favor, permite las ventanas emergentes.');
                return;
            }

            // Escribir HTML y abrir diálogo de impresión
            printWindow.document.write(printHTML);
            printWindow.document.close();

            // Esperar a que se cargue antes de imprimir
            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
            }, 500);

        } catch (error) {
            console.error('Error al imprimir:', error);
            alert('Hubo un error al generar la impresión. Por favor, intenta de nuevo.');
        }
    }, [filteredEmployees, weeklySchedules, schedules, currentWeek, getFilterDepartmentName]);

    // Función para copiar la configuración de la semana actual al resto de semanas del año
    const handleCopyWeekToAllWeeks = useCallback(async () => {
        if (!confirm('¿Estás seguro de que quieres copiar esta configuración a todas las semanas del año? Esta acción sobrescribirá las asignaciones existentes.')) {
            return;
        }

        try {
            setIsCopying(true);
            setError(null);

            // Verificar si hay asignaciones en la semana actual
            if (weeklySchedules.length === 0) {
                setError('No hay asignaciones en la semana actual para copiar.');
                return;
            }

            // Obtener todas las semanas del año actual
            const currentYear = new Date().getFullYear();
            const yearStart = startOfWeek(new Date(currentYear, 0, 1), { weekStartsOn: 1 });
            const yearEnd = startOfWeek(new Date(currentYear, 11, 31), { weekStartsOn: 1 });

            const weeksToCopy = [];
            let currentWeekDate = yearStart;

            while (currentWeekDate <= yearEnd) {
                const currentWeekString = format(currentWeekDate, 'yyyy-MM-dd');

                // No copiar a la semana actual
                if (currentWeekString !== weekStartString) {
                    weeksToCopy.push(currentWeekString);
                }

                currentWeekDate = addWeeks(currentWeekDate, 1);
            }

            if (weeksToCopy.length === 0) {
                setError('No hay semanas adicionales a las que copiar (solo existe la semana actual).');
                return;
            }

            // Copiar a cada semana
            const employeeIds = filteredEmployees.map(emp => emp.id);
            let successCount = 0;
            let errorCount = 0;

            for (const targetWeekStart of weeksToCopy) {
                try {
                    await weeklyScheduleApi.copyWeekToWeek({
                        sourceWeekStart: weekStartString,
                        targetWeekStart: targetWeekStart,
                        employeeIds
                    });
                    successCount++;
                } catch (error) {
                    console.error(`Error copiando a semana ${targetWeekStart}:`, error);
                    errorCount++;
                }
            }

            if (errorCount > 0) {
                setError(`Se copió la configuración a ${successCount} semanas, pero hubo ${errorCount} errores.`);
            } else {
                alert(`Configuración copiada exitosamente a ${successCount} semanas del año.`);
                // Recargar datos para mostrar los cambios
                await loadData();
            }

        } catch (err) {
            console.error('Error al copiar semana a todas las semanas:', err);
            setError('No se pudo copiar la configuración a todas las semanas');
        } finally {
            setIsCopying(false);
        }
    }, [weekStartString, employees, weeklySchedules, loadData]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader />
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
                {error}
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            {/* Cabecera con navegación y horarios disponibles */}
            <div className="mb-6">
                <div className="flex justify-between items-center mb-4 no-print">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white">
                        Calendario Semanal - {format(currentWeek, 'dd MMM yyyy', { locale: es })}
                    </h2>
                    <div className="flex gap-2 flex-wrap items-end">
                        <Button
                            variant="outline"
                            onClick={() => setCurrentWeek(addWeeks(currentWeek, -1))}
                            disabled={isLoading}
                        >
                            ← Semana Anterior
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setCurrentWeek(addWeeks(currentWeek, 1))}
                            disabled={isLoading}
                        >
                            Siguiente Semana →
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={handleCopyWeekToAllWeeks}
                            disabled={isCopying || isLoading || weeklySchedules.length === 0}
                            className="bg-green-600 hover:bg-green-700 text-white"
                        >
                            {isCopying ? (
                                <>
                                    <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                                    Copiando...
                                </>
                            ) : (
                                <>
                                    📋 Copiar a Todas las Semanas
                                </>
                            )}
                        </Button>
                    </div>
                </div>

                 {/* Print controls - no-print */}
                 <div className="flex items-center gap-4 mb-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg no-print">
                     <Button
                         variant="primary"
                         onClick={handleOpenPrintModal}
                         icon={<span className="text-lg">🖨️</span>}
                         disabled={isLoading}
                     >
                         Imprimir Calendario
                     </Button>
                        <Button
                            variant={selectionMode ? 'primary' : 'outline'}
                            onClick={() => {
                                setSelectionMode(!selectionMode);
                                setSourceEmployeeId(null);
                                setSourceDay(null);
                                setSourceSchedules(null);
                                setDestinationDays(new Set());
                            }}
                            icon={<span className="text-lg">📋</span>}
                        >
                            {selectionMode ? 'Salir Modo Selección' : 'Modo Selección'}
                        </Button>
                       {selectionMode && sourceSchedules && (
                           <Button
                               variant="primary"
                               onClick={handleCopyToDestinations}
                               disabled={destinationDays.size === 0}
                               className="bg-blue-600 hover:bg-blue-700"
                           >
                               {`Copiar ${sourceSchedules.length} Turno(s) a ${destinationDays.size} Día(s)`}
                           </Button>
                       )}
                       <p className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                           Total empleados: {filteredEmployees.length}
                       </p>
                        {selectionMode && (
                           <div className="absolute top-0 left-0 right-0 bottom-0 -z-10 bg-blue-50 dark:bg-blue-900/30 border-b-4 border-blue-400 dark:border-blue-600 p-4 flex items-center justify-center">
                               <div className="text-center">
                                   <p className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">
                                       {!sourceEmployeeId ? 'Selecciona un día de origen (verde)' : 'Selecciona días de destino (rojo)'}
                                   </p>
                                   <p className="text-sm text-blue-700 dark:text-blue-300">
                                       {sourceEmployeeId
                                           ? `${filteredEmployees.find(e => e.id === sourceEmployeeId)?.name || ''}: Origen ${weekDays[sourceDay!].dayName} → ${destinationDays.size} destino(s)`
                                           : 'Click en un día con turnos para marcar como origen'
                                       }
                                   </p>
                               </div>
                           </div>
                       )}
                  </div>

                 {/* Print modal */}
                <PrintModal
                    isOpen={printModalOpen}
                    onClose={() => setPrintModalOpen(false)}
                    onPrint={handlePrint}
                    employeeCount={filteredEmployees.length}
                />

                {/* Lista de horarios disponibles - no-print */}
                <div className="mb-6 no-print">
                    <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300 dark:text-gray-300 mb-3">Horarios Disponibles</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 p-4 rounded-lg border-2 border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                        {/* Horario de descanso */}
                        <div
                            onClick={() => {
                                const newSelectedSchedule = selectedSchedule === 'REST' ? null : 'REST';
                                setSelectedSchedule(newSelectedSchedule);
                            }}
                            className={`border rounded p-2 mb-2 cursor-pointer hover:opacity-80 transition-colors ${selectedSchedule === 'REST' ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                                }`}
                            style={{
                                backgroundColor: '#f3f4f6',
                                borderColor: selectedSchedule === 'REST' ? '#3b82f6' : '#d1d5db',
                                color: '#374151'
                            }}
                        >
                            <div className={`font-medium text-sm`}>
                                Descanso
                            </div>
                            <div className={`text-xs`}>
                                Día libre
                            </div>
                        </div>

                        {[...schedules].sort((a, b) => a.startTime.localeCompare(b.startTime)).map((schedule) => (
                            <div
                                key={schedule.id}
                                onClick={() => setSelectedSchedule(selectedSchedule === schedule.id ? null : schedule.id)}
                                className={`border rounded p-2 mb-2 cursor-pointer hover:opacity-80 transition-colors ${selectedSchedule === schedule.id ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                                    }`}
                                style={getScheduleStyles(schedule)}
                            >
                                <div className={`font-medium text-sm`}>
                                    {schedule.name}
                                </div>
                                <div className={`text-xs`}>
                                    {schedule.startTime} - {schedule.endTime}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Calendario semanal */}
            <div className="overflow-x-auto">
                <table className="w-full border-collapse print-calendar">
                    <thead>
                        <tr>
                            <th className="border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 p-3 text-left font-semibold text-gray-700 dark:text-gray-300 min-w-[150px]">
                                Empleado
                            </th>
                            <th className="border border-gray-300 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 p-3 text-left font-semibold text-gray-700 dark:text-gray-300 min-w-[100px] no-print">
                                Departamento
                            </th>
                              {weekDays.map(day => {
                                    const isSource = sourceDay === day.dayOfWeek;
                                    const isDestination = destinationDays.has(day.dayOfWeek);
                                    let bgClass = 'bg-gray-100 dark:bg-gray-800';
                                    let borderClass = 'border-gray-300 dark:border-gray-700';
                                    let ringClass = '';
                                    let indicator = null;

                                    if (selectionMode && sourceEmployeeId) {
                                        if (isSource) {
                                            bgClass = 'bg-green-100 dark:bg-green-900/40';
                                            borderClass = 'border-green-400 dark:border-green-600';
                                            ringClass = 'ring-2 ring-green-400 ring-offset-2';
                                            indicator = (
                                                <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-md z-10" title="Día origen de copia">
                                                    ✓
                                                </div>
                                            );
                                        } else if (isDestination) {
                                            bgClass = 'bg-red-100 dark:bg-red-900/40';
                                            borderClass = 'border-red-400 dark:border-red-600';
                                            ringClass = 'ring-2 ring-red-400 ring-offset-2';
                                            indicator = (
                                                <div className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-md z-10" title="Día destino de copia">
                                                    ↗
                                                </div>
                                            );
                                        }
                                    }

                                    return (
                                        <th key={day.dayOfWeek} className={`border ${borderClass} ${bgClass} p-3 text-center font-semibold text-gray-700 dark:text-gray-300 min-w-[120px] relative ${ringClass}`}>
                                            {indicator}
                                          <div>{day.dayName}</div>
                                         <div className="text-sm font-normal text-gray-600 dark:text-gray-400">
                                              {format(new Date(day.date), 'dd/MM')}
                                          </div>
                                      </th>
                                    );
                               })}
                            <th className="border border-gray-300 bg-blue-100 dark:bg-blue-900/30 p-3 text-center font-semibold text-blue-800 dark:text-blue-400 min-w-[100px] total-cell">
                                Total Semana
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredEmployees.map(employee => (
                            <tr key={employee.id}>
                                <td className="border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 font-medium text-gray-800 dark:text-white employee-cell">
                                    {employee.name}
                                </td>
                                <td className="border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 p-3 text-sm text-gray-600 dark:text-gray-400 department-cell">
                                    {employee.departmentModel?.name || '-'}
                                </td>
                                 {weekDays.map(day => {
                                       const daySchedules = getWeeklySchedulesForEmployeeAndDay(employee.id, day.dayOfWeek);
                                       const dayAbsences = getAbsencesForEmployeeAndDate(employee.id, day.date);
                                       const isSource = sourceEmployeeId === employee.id && sourceDay === day.dayOfWeek;
                                       const isDestination = sourceEmployeeId === employee.id && destinationDays.has(day.dayOfWeek);
                                       let cellBgColor = 'transparent';
                                       if (selectionMode && sourceEmployeeId === employee.id) {
                                           if (isSource) cellBgColor = 'rgba(34, 197, 94, 0.15)';
                                           else if (isDestination) cellBgColor = 'rgba(239, 68, 68, 0.15)';
                                       } else if (selectedSchedule) {
                                           cellBgColor = '#f0f9ff';
                                       }
                                       return (
                                             <td
                                                key={day.dayOfWeek}
                                                className={`border border-gray-300 p-2 relative ${selectionMode || selectedSchedule ? 'cursor-pointer' : ''}`}
                                                onClick={() => handleCellClick(employee.id, day.dayOfWeek)}
                                               style={{
                                                   backgroundColor: cellBgColor
                                               }}
                                            >
                                             {[...daySchedules].sort((a, b) => {
                                                 // Días de descanso van al final
                                                 if (a.scheduleId === null) return 1;
                                                 if (b.scheduleId === null) return -1;
                                                 // Ordenar por hora de inicio
                                                 const startA = a.schedule?.startTime || '00:00';
                                                 const startB = b.schedule?.startTime || '00:00';
                                                 return startA.localeCompare(startB);
                                             }).map((weeklySchedule) => {
                                                 // Verificar si es una asignación de descanso
                                                 const isRestDay = weeklySchedule.scheduleId === null;

                                                  if (isRestDay) {
                                                          return (
                                                              <div
                                                                  key={weeklySchedule.id}
                                                                  className="border rounded p-2 mb-2 relative group rest-day-block"
                                                                  style={{
                                                                      backgroundColor: '#f3f4f6',
                                                                      borderColor: '#d1d5db',
                                                                      color: '#374151'
                                                                  }}
                                                              >
                                                                  {!selectionMode && (
                                                                      <button
                                                                          onClick={(e) => {
                                                                              e.stopPropagation();
                                                                              handleRemoveWeeklySchedule(weeklySchedule.id);
                                                                          }}
                                                                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity no-print"
                                                                          title="Eliminar asignación"
                                                                      >
                                                                          ×
                                                                      </button>
                                                                  )}
                                                                  <div className="font-medium text-sm">
                                                                      Descanso
                                                                  </div>
                                                                  <div className="text-xs">
                                                                      Día libre
                                                                  </div>
                                                                  {weeklySchedule.notes && (
                                                                      <div className="text-xs opacity-75 mt-1">
                                                                          {weeklySchedule.notes}
                                                                      </div>
                                                                  )}
                                                              </div>
                                                          );
                                                     } else {
                                                           // Renderizar horario normal
                                                           return (
                                                                <div
                                                                    key={weeklySchedule.id}
                                                                   className="border rounded p-2 mb-2 relative group schedule-block"
                                                                 style={getScheduleStyles(weeklySchedule.schedule || {
                                                                     id: '',
                                                                     name: 'Horario',
                                                                     startTime: '00:00',
                                                                     endTime: '00:00',
                                                                     color: '#3B82F6',
                                                                     active: true
                                                                 })}
                                                             >
                                                                 {!selectionMode && (
                                                                     <button
                                                                         onClick={(e) => {
                                                                             e.stopPropagation();
                                                                             handleRemoveWeeklySchedule(weeklySchedule.id);
                                                                         }}
                                                                         className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity no-print"
                                                                         title="Eliminar asignación"
                                                                     >
                                                                         ×
                                                                     </button>
                                                                 )}
                                                                 <div className="font-medium text-sm flex items-center gap-1">
                                                                     {weeklySchedule.schedule?.name || 'Horario'}
                                                                     {weeklySchedule.schedule?.startTime && weeklySchedule.schedule?.endTime &&
                                                                         isNightShift(weeklySchedule.schedule.startTime, weeklySchedule.schedule.endTime) && (
                                                                             <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 no-print" title="Turno nocturno">
                                                                                 🌙
                                                                             </span>
                                                                         )}
                                                                 </div>
                                                                 <div className="text-xs">
                                                                     {weeklySchedule.schedule?.startTime} - {weeklySchedule.schedule?.endTime}
                                                                 </div>
                                                                 {weeklySchedule.notes && (
                                                                     <div className="text-xs opacity-75 mt-1">
                                                                         {weeklySchedule.notes}
                                                                     </div>
                                                                 )}
                                                             </div>
                                                         );
                                                   }
                                             })}
                                             {dayAbsences.map(absence => (
                                                 <div
                                                     key={absence.id}
                                                     className={`border rounded p-2 mb-2 relative group absence-block`}
                                                     style={{
                                                         backgroundColor: '#fef3c7',
                                                         borderColor: '#fbbf24',
                                                         color: '#92400e'
                                                     }}
                                                 >
                                                     <div className="font-medium text-sm">
                                                         {getAbsenceTypeLabel(absence.type)}
                                                     </div>
                                                     <div className="text-xs opacity-75">
                                                         {absence.days} día{absence.days !== 1 ? 's' : ''}
                                                     </div>
                                                     {absence.reason && (
                                                         <div className="text-xs opacity-75 mt-1">
                                                             {absence.reason}
                                                         </div>
                                                     )}
                                                 </div>
                                             ))}
                                               {daySchedules.length === 0 && dayAbsences.length === 0 && (
                                                <div className={`text-gray-400 text-sm text-center py-4 ${selectedSchedule === 'REST' ? 'text-blue-600 font-medium' : ''}`}>
                                                    {selectedSchedule === 'REST' ? 'Click para marcar como descanso' : selectedSchedule ? 'Click para asignar horario' : 'Selecciona un horario arriba'}
                                                </div>
                                            )}
                                        </td>
                                    );
                                })}
                                <td className="border border-gray-300 bg-blue-50 dark:bg-blue-900/30 p-3 text-center font-semibold text-blue-800 dark:text-blue-400">
                                    {calculateEmployeeWeeklyHours(employee.id) > 0 ? `${calculateEmployeeWeeklyHours(employee.id).toFixed(1)}h` : '-'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="bg-gray-100 dark:bg-gray-800 font-semibold dark:text-white total-cell">
                            <td className="border border-gray-300 dark:border-gray-700 p-3 text-gray-800 dark:text-white" colSpan={2}>
                                Total Horas
                            </td>
                            {weekDays.map(day => {
                                const dailyHours = calculateDailyHours(day.dayOfWeek);
                                return (
                                    <td key={day.dayOfWeek} className="border border-gray-300 dark:border-gray-700 p-3 text-center text-gray-800 dark:text-white">
                                        {dailyHours > 0 ? `${dailyHours.toFixed(1)}h` : '-'}
                                    </td>
                                );
                            })}
                            <td className="border border-gray-300 p-3 text-center bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400 font-bold total-cell">
                                {calculateWeeklyTotalHours() > 0 ? `${calculateWeeklyTotalHours().toFixed(1)}h` : '-'}
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {filteredEmployees.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400 no-print">
                    No hay empleados para mostrar en el calendario
                </div>
            )}

            {selectedSchedule && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded">
                    <p className="text-sm text-blue-700">
                        {selectedSchedule === 'REST' ? (
                            <strong>Día de descanso seleccionado</strong>
                        ) : (
                            <>
                                Horario seleccionado: <strong>{schedules.find(s => s.id === selectedSchedule)?.name}</strong>
                            </>
                        )}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                        {selectedSchedule === 'REST'
                            ? 'Haz clic en cualquier celda del calendario para marcar como día de descanso'
                            : 'Haz clic en cualquier celda del calendario para asignar este horario'
                        }
                    </p>
                </div>
            )}
        </div>
    );
};

export default WeeklyCalendarSimple;